import React from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';

interface DustParticlesProps {
    isActive: boolean;
    isCurrentUser: boolean;
    width: number;
    height: number;
    left?: number; // Exact left position of message
    top?: number; // Exact top position of message
}

interface Particle {
    id: number;
    x: number;
    y: number;
    rotate: number;
    delay: number;
    scale: number;
    opacity: number;
    duration: number;
}

// Generate a grid of particles with diverse behaviors
const generateParticles = (rows: number, cols: number): Particle[] => {
    const particles: Particle[] = [];
    for (let i = 0; i < rows * cols; i++) {
        // More random parameters for diverse movement
        const delay = Math.random() * 0.7;
        // Wider spread with direction bias - some up, some to sides
        const direction = Math.random();
        let x, y;

        if (direction < 0.3) {
            // Upward movement
            x = (Math.random() - 0.5) * 200;
            y = -Math.random() * 300 - 50;
        } else if (direction < 0.6) {
            // Side movement
            x = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 300 + 100);
            y = (Math.random() - 0.5) * 150;
        } else {
            // Diagonal movement
            x = (Math.random() - 0.5) * 300;
            y = -Math.random() * 250 - 50;
        }

        const rotate = (Math.random() - 0.5) * 720; // More rotation
        const scale = Math.random() * 0.5; // Different end scales
        const particleOpacity = Math.random() * 0.7; // Different fade rates
        const duration = 1 + Math.random() * 1.5; // Different durations

        particles.push({
            id: i,
            x,
            y,
            rotate,
            delay,
            scale,
            opacity: particleOpacity,
            duration,
        });
    }
    return particles;
};

const PARTICLES: Particle[] = generateParticles(20, 20); // 400 particles

const DustParticles: React.FC<DustParticlesProps> = ({ isActive, isCurrentUser, width, height, left = 0, top = 0 }) => {
    const { themeStyle } = useThemeContext();

    // Calculate the size of each particle with more variation
    const getParticleSize = () => {
        const baseSize = Math.max(2, Math.min(width, height) / 50);
        return baseSize * (0.5 + Math.random() * 1.5); // 50% to 200% of base size
    };

    // Calculate grid positioning with a slight jitter
    const cols = 20;
    const rows = 20;
    const cellWidth = width / cols;
    const cellHeight = height / rows;

    return (
        <Box
            sx={{
                position: 'fixed', // Use fixed to ensure particles escape message container
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 9999,
            }}>
            <AnimatePresence>
                {isActive &&
                    PARTICLES.map(({ id, x, y, rotate, delay, scale, opacity, duration }) => {
                        // Calculate grid position with offset
                        const row = Math.floor(id / cols);
                        const col = id % cols;

                        // Add minor jitter for natural look
                        const jitterX = (Math.random() - 0.5) * cellWidth * 0.5;
                        const jitterY = (Math.random() - 0.5) * cellHeight * 0.5;

                        const particleSize = getParticleSize();

                        // Use different shades based on position
                        const baseColor = isCurrentUser
                            ? theme[themeStyle].tertiary_green
                            : theme[themeStyle].deep_grey;

                        // Position particles exactly at the message's location
                        const startX = left + col * cellWidth + jitterX;
                        const startY = top + row * cellHeight + jitterY;

                        return (
                            <motion.div
                                key={id}
                                initial={{
                                    opacity: 1,
                                    scale: 1,
                                    x: startX,
                                    y: startY,
                                    rotate: 0,
                                    backgroundColor: baseColor,
                                    boxShadow: '0px 0px 2px rgba(0,0,0,0.2)',
                                }}
                                animate={{
                                    opacity: 0,
                                    scale,
                                    x: startX + x,
                                    y: startY + y,
                                    rotate,
                                    backgroundColor: baseColor,
                                    boxShadow: '0px 0px 3px rgba(0,0,0,0.1)',
                                    transition: {
                                        duration,
                                        delay,
                                        ease: 'easeOut',
                                    },
                                }}
                                style={{
                                    width: particleSize,
                                    height: particleSize,
                                    borderRadius: '50%',
                                    position: 'absolute',
                                }}
                            />
                        );
                    })}
            </AnimatePresence>
        </Box>
    );
};

export default DustParticles;
